import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import BackToTopButton from '../../components/feature/BackToTopButton';

const FaqPage = () => {
  useEffect(() => {
    document.title = 'Preguntas Frecuentes - PLADUREX | Dudas sobre Pladur en Mataró';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Resuelve tus dudas sobre instalación de pladur en Mataró: plazos, precios, aislamientos, acabados y más. Respuestas claras de profesionales.');
    }

    // Add FAQPage Schema.org
    const existingFAQSchema = document.querySelector('script[data-schema="faqpage"]');
    if (existingFAQSchema) existingFAQSchema.remove();
    
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/faq#faqpage`,
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
    
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.setAttribute('data-schema', 'faqpage');
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    // Add BreadcrumbList Schema.org
    const existingSchema = document.querySelector('script[data-schema="breadcrumb"]');
    if (existingSchema) existingSchema.remove();
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Inicio",
          "item": import.meta.env.VITE_SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Preguntas Frecuentes",
          "item": `${import.meta.env.VITE_SITE_URL}/faq`
        }
      ]
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'breadcrumb');
    script.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="breadcrumb"]');
      if (schemaScript) schemaScript.remove();
      const faqSchemaScript = document.querySelector('script[data-schema="faqpage"]');
      if (faqSchemaScript) faqSchemaScript.remove();
    };
  }, []);

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: '¿Cuánto tiempo tarda una instalación de pladur?',
      answer: 'Los plazos varían según el tipo de trabajo. Un tabique estándar de una habitación se puede instalar en 1-2 días. Los trasdosados de una estancia tardan entre 2-3 días. Los falsos techos dependen de los m² y la complejidad, pero una habitación media se completa en 2-3 días. Las reformas completas se planifican según el alcance del proyecto.',
      link: '/servicios#tabiques',
      linkText: 'Ver servicio de tabiques'
    },
    {
      question: '¿Qué factores afectan al precio de una instalación de pladur?',
      answer: 'El precio depende de varios factores: los metros cuadrados a instalar, la altura de los techos, el tipo de placa (estándar, antihumedad, con refuerzo acústico), si lleva aislamiento y qué tipo, la complejidad de la instalación (esquinas, curvas, instalaciones), el nivel de acabado y la accesibilidad de la zona de trabajo.',
      link: '/contacto',
      linkText: 'Solicitar presupuesto'
    },
    {
      question: '¿El pladur aísla del ruido realmente?',
      answer: 'Sí, el pladur con el sistema adecuado ofrece excelente aislamiento acústico. Para conseguir buenos resultados se utiliza doble placa, lana de roca de alta densidad entre las placas, perfiles antivibratorios y banda elástica perimetral. Un sistema bien ejecutado puede reducir el ruido entre 45-55 dB, suficiente para aislar dormitorios, salas de música o zonas de trabajo.',
      link: '/servicios#acustico',
      linkText: 'Ver aislamiento acústico'
    },
    {
      question: '¿Se puede usar pladur en baños y cocinas?',
      answer: 'Sí, para zonas húmedas utilizamos placas de pladur antihumedad (WA) que tienen un núcleo tratado que resiste la humedad y el vapor. Estas placas son perfectas para baños, cocinas, lavaderos y cualquier zona con alta humedad ambiental. Se pueden alicatar o pintar sin problemas.',
      link: '/servicios#antihumedad',
      linkText: 'Ver pladur antihumedad'
    },
    {
      question: '¿El pladur mejora el aislamiento térmico?',
      answer: 'Sí, instalando un trasdosado con aislante térmico (lana mineral, poliestireno o XPS) se mejora significativamente la eficiencia energética. Esto reduce las pérdidas de calor en invierno y mantiene el frescor en verano, lo que se traduce en menor consumo de calefacción y aire acondicionado.',
      link: '/servicios#termico',
      linkText: 'Ver aislamiento térmico'
    },
    {
      question: '¿Los acabados quedan listos para pintar?',
      answer: 'Sí, todos nuestros trabajos se entregan con acabados perfectos listos para pintar. Realizamos el tratamiento de juntas con cinta y pasta, lijamos las superficies y dejamos las paredes y techos completamente lisos. Solo tienes que aplicar la pintura que prefieras.',
      link: '/empresa',
      linkText: 'Conoce nuestra forma de trabajar'
    },
    {
      question: '¿Qué molestias genera la obra?',
      answer: 'El pladur es obra seca, por lo que genera menos molestias que la construcción tradicional. No hay agua ni escombros pesados. El polvo es mínimo y se controla con aspiración. Trabajamos en horarios acordados, protegemos suelos y muebles, y limpiamos al final de cada jornada. Muchos clientes siguen viviendo en casa durante la reforma.',
      link: '/empresa',
      linkText: 'Ver cómo trabajamos'
    },
    {
      question: '¿Se necesitan permisos para instalar pladur?',
      answer: 'Para trabajos interiores que no afectan a la estructura del edificio (tabiques, trasdosados, techos) normalmente no se necesitan permisos especiales. Sin embargo, si la reforma implica cambios en la distribución que afecten a elementos comunes o estructurales, puede ser necesario comunicarlo a la comunidad o solicitar licencia. Te asesoramos en cada caso.',
      link: '/contacto',
      linkText: 'Consultar con nosotros'
    },
    {
      question: '¿Qué incluye el presupuesto?',
      answer: 'Nuestros presupuestos incluyen todo lo necesario: materiales (placas, perfilería, aislantes, pasta de juntas), mano de obra cualificada, transporte, montaje, tratamiento de juntas, acabados y limpieza. Te entregamos el trabajo listo para pintar. No hay costes ocultos ni sorpresas.',
      link: '/contacto',
      linkText: 'Pedir presupuesto gratis'
    },
    {
      question: '¿Dais factura y garantía?',
      answer: 'Sí, todos nuestros trabajos se facturan correctamente. Ofrecemos garantía sobre la instalación y los materiales utilizados. Si surge cualquier problema relacionado con nuestro trabajo, lo solucionamos sin coste adicional.',
      link: '/empresa',
      linkText: 'Conoce nuestras garantías'
    }
  ];

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-[#F3F3F3]">
      <Header />
      <WhatsAppButton />

      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FF007A]/10 via-[#0B0B0F] to-[#0B0B0F]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 break-words">
              <strong>Preguntas frecuentes</strong>
              <span className="block mt-2 text-[#FF007A]">sobre pladur en Mataró</span>
            </h1>
            <p className="text-lg text-[#F3F3F3]/90 max-w-3xl mx-auto leading-relaxed break-words">
              Resuelve tus dudas sobre <strong>instalación de pladur en Mataró</strong>. Consulta nuestros <Link to="/servicios" className="text-[#FF007A] hover:underline">servicios de pladur</Link> o llámanos al 629 501 941.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#12121A] rounded-xl border border-[#FF007A]/10 overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-4 sm:px-6 py-5 flex items-center justify-between text-left hover:bg-[#1A1A24] transition-colors duration-200 cursor-pointer"
                >
                  <h3 className="text-base sm:text-lg font-semibold pr-4 break-words">{faq.question}</h3>
                  <motion.i
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="ri-arrow-down-s-line text-2xl text-[#FF007A] flex-shrink-0"
                  ></motion.i>
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 sm:px-6 pb-5 pt-2">
                        <p className="text-[#F3F3F3]/70 leading-relaxed break-words mb-3">{faq.answer}</p>
                        <Link 
                          to={faq.link} 
                          className="inline-flex items-center text-sm text-[#FF007A] hover:underline cursor-pointer"
                        >
                          {faq.linkText}
                          <i className="ri-arrow-right-line ml-1"></i>
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#12121A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 break-words">
              ¿Tienes más <span className="text-[#FF007A]">preguntas?</span>
            </h2>
            <p className="text-base sm:text-lg text-[#F3F3F3]/70 mb-8 max-w-2xl mx-auto break-words">
              Si no has encontrado la respuesta que buscabas, <Link to="/contacto" className="text-[#FF007A] hover:underline">contacta con nosotros</Link>. Estaremos encantados de resolver todas tus dudas sobre tu proyecto de pladur. También puedes <Link to="/empresa" className="text-[#FF007A] hover:underline">conocer más sobre nuestra empresa</Link>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="px-8 py-4 bg-[#FF007A] text-white rounded-lg text-base font-semibold hover:bg-[#FF4DA6] transition-all duration-300 shadow-lg hover:shadow-[#FF007A]/50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                629 501 941
              </a>
              <Link
                to="/contacto"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg text-base font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20 whitespace-nowrap cursor-pointer"
              >
                Formulario de contacto
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BackToTopButton />
    </div>
  );
};

export default FaqPage;
